/**
 * Kiosk Confirmation Page - Phase 1F Enhanced
 * 
 * Features:
 * - Large order success indicator with checkmark animation
 * - Prominent order number (easy to read from distance)
 * - Simple confirmation message
 * - Optional SMS notification message
 * - Auto-reset timer (15 seconds with countdown)
 * - Lightweight upsell modal (skippable)
 * - Immediate "Start New Order" button
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageSquare, CreditCard, Receipt } from 'lucide-react';
import { useKiosk } from '../context/KioskContext';
import UpsellModal from '../components/upsell/UpsellModal';

const AUTO_RESET_SECONDS = 15;

export default function ConfirmationPage() {
  const { 
    order, 
    customerName, 
    customerPhone,
    menu,
    resetSession,
    setSelectedItem,
    kioskApi,
  } = useKiosk();
  const navigate = useNavigate();

  const [countdown, setCountdown] = useState(AUTO_RESET_SECONDS);
  const [showUpsell, setShowUpsell] = useState(false);
  // Text me a receipt: the countdown waits while the guest types; the answer is the SMS service's
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptPhone, setReceiptPhone] = useState(customerPhone || '');
  const [receiptState, setReceiptState] = useState(null); // {busy} | {ok, text}
  const paused = useRef(false);
  paused.current = receiptOpen || !!receiptState?.busy;

  const handleReset = useLatestCallback(() => {
    resetSession();
    navigate('../');
  });

  // Auto-reset timer with countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (paused.current) return prev;
        if (prev <= 1) {
          handleReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [handleReset]);

  const sendReceipt = async () => {
    if (!order?.order_id) return;
    setReceiptState({ busy: true });
    const key = `kiosk-receipt-${window.crypto?.randomUUID ? window.crypto.randomUUID() : Date.now()}`;
    try {
      const answer = await kioskApi('post', `/order/${order.order_id}/receipt/text`, { phone: receiptPhone }, { 'Idempotency-Key': key });
      setReceiptState({ ok: !!answer.sent, text: answer.sent ? 'Receipt texted. Check your phone.' : (answer.message || 'The text was not sent.') });
      if (answer.sent) { setReceiptOpen(false); setCountdown(AUTO_RESET_SECONDS); }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setReceiptState({ ok: false, text: typeof detail === 'string' ? detail : 'The text was not sent. Please ask staff for a receipt.' });
    }
  };

  const handleSkipUpsell = () => {
    setShowUpsell(false);
  };

  const handleSelectUpsellItem = (item) => {
    setShowUpsell(false);
    // Open item detail modal for the upsell item
    setSelectedItem(item);
    // Navigate back to menu to show the item detail modal
    navigate('../menu');
  };

  // Get relevant upsell items (drinks, desserts, add-ons)
  const getUpsellItems = useCallback(() => {
    if (!menu?.categories) return [];
    
    // Look for Beverages or Dessert categories
    const upsellCategories = menu.categories.filter(cat => 
      cat.name.toLowerCase().includes('beverage') || 
      cat.name.toLowerCase().includes('drink') ||
      cat.name.toLowerCase().includes('dessert')
    );

    const items = [];
    for (const category of upsellCategories) {
      if (category.items && category.items.length > 0) {
        // Get first 2 available items
        const availableItems = category.items
          .filter(item => item.is_available)
          .slice(0, 2 - items.length);
        items.push(...availableItems);
        if (items.length >= 2) break;
      }
    }

    return items;
  }, [menu]);

  // Show upsell after 3 seconds (after confirmation is clear)
  useEffect(() => {
    const upsellTimer = setTimeout(() => {
      const upsellItems = getUpsellItems();
      if (upsellItems.length > 0) {
        setShowUpsell(true);
      }
    }, 3000);

    return () => clearTimeout(upsellTimer);
  }, [getUpsellItems]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #1e2330 0%, #1a2e1a 100%)' }}
    >
      <div className="text-center text-white max-w-3xl px-8">
        {/* Success Icon with Animation */}
        <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-emerald-500/20 flex items-center justify-center animate-scale-in">
          <CheckCircle2 className="w-20 h-20 text-emerald-400 animate-check" strokeWidth={2.5} />
        </div>

        {/* Confirmation Message */}
        <h1 className="text-5xl font-bold mb-3 text-white">
          Your order has been placed
        </h1>
        
        {/* Order Number - VERY PROMINENT */}
        {order?.order_number && (
          <div className="my-10">
            <p className="text-xl text-white/50 mb-3 tracking-wide uppercase">
              Order Number
            </p>
            <div 
              className="inline-block px-12 py-8 rounded-3xl border-4 border-[#f5a623] shadow-2xl"
              style={{ background: 'rgba(245, 166, 35, 0.15)' }}
            >
              <p 
                className="text-9xl font-black tracking-wider"
                style={{ color: '#f5a623', textShadow: '0 4px 20px rgba(245, 166, 35, 0.3)' }}
              >
                {order.order_number}
              </p>
            </div>
          </div>
        )}

        {/* What the card was charged (the gateway's answer, incl. any card fee) */}
        {order?.charged_cents > 0 && (
          <div className="flex items-center justify-center gap-3 text-xl text-white/80 mb-2" data-testid="kiosk-confirmation-paid">
            <CreditCard size={22} className="text-emerald-400" />
            <span>Paid <span className="font-semibold text-white">${(order.charged_cents / 100).toFixed(2)}</span></span>
            {order.last_four && <span className="text-white/50">· {order.card_type || 'Card'} •••• {order.last_four}</span>}
          </div>
        )}
        {order?.fee_cents > 0 && (
          <p className="text-sm text-white/45 mb-2">Includes ${(order.fee_cents / 100).toFixed(2)} card fee</p>
        )}

        {/* Pickup Instructions */}
        <div className="mt-8 mb-8">
          <p className="text-2xl text-white/80 mb-2">
            Please wait for your number
          </p>
          {order?.estimated_time && (
            <p className="text-lg text-white/50">
              Estimated wait: {order.estimated_time} minutes
            </p>
          )}
        </div>

        {/* SMS Notification (if phone provided) */}
        {customerPhone && (
          <div className="flex items-center justify-center gap-2 text-white/60 mb-8">
            <MessageSquare size={20} />
            <p className="text-base">
              We'll text you when ready
            </p>
          </div>
        )}

        {/* Text me a receipt */}
        {order?.order_id && order?.payment_approved && (
          <div className="mb-8 flex flex-col items-center gap-3" data-testid="kiosk-receipt">
            {!receiptOpen && !receiptState?.ok && (
              <button type="button" onClick={() => { setReceiptOpen(true); setReceiptState(null); }}
                className="h-14 px-8 rounded-2xl border border-white/20 text-white text-lg font-semibold flex items-center gap-3 hover:bg-white/10">
                <Receipt size={22} /> Text me a receipt
              </button>
            )}
            {receiptOpen && (
              <div className="flex gap-3 w-full max-w-md">
                <input inputMode="tel" aria-label="Mobile number" value={receiptPhone} placeholder="Mobile number"
                  onChange={(e) => setReceiptPhone(e.target.value.replace(/[^0-9+()\- ]/g, '').slice(0, 20))}
                  className="flex-1 h-14 rounded-2xl px-5 text-xl text-[#0f1419] bg-white" />
                <button type="button" onClick={sendReceipt} disabled={receiptState?.busy || receiptPhone.replace(/\D/g, '').length < 10}
                  className="h-14 px-6 rounded-2xl bg-[#f5a623] text-[#0f1419] text-lg font-semibold disabled:opacity-50">
                  {receiptState?.busy ? 'Sending…' : 'Send'}
                </button>
              </div>
            )}
            {receiptState && !receiptState.busy && (
              <p role="status" className={`text-base ${receiptState.ok ? 'text-emerald-300' : 'text-red-300'}`}>{receiptState.text}</p>
            )}
          </div>
        )}

        {/* Start New Order Button */}
        <button
          onClick={handleReset}
          className="
            h-16 px-12 rounded-2xl
            bg-[#f5a623] text-[#0f1419] text-xl font-semibold
            hover:brightness-110 active:scale-[0.98]
            transition-all duration-150
            shadow-lg
          "
        >
          Start New Order
        </button>

        {/* Auto-Reset Countdown */}
        <p className="mt-6 text-base text-white/40">
          Starting new order in {countdown} second{countdown !== 1 ? 's' : ''}...
        </p>

        {/* Progress Bar */}
        <div 
          className="mt-4 w-64 mx-auto h-1.5 rounded-full overflow-hidden"
          style={{ background: 'rgba(245, 166, 35, 0.2)' }}
        >
          <div 
            className="h-full bg-[#f5a623]"
            style={{ 
              animation: `shrink-bar ${AUTO_RESET_SECONDS}s linear forwards`,
            }}
          />
        </div>
      </div>

      {/* Upsell Modal - Shows after 3 seconds */}
      <UpsellModal
        items={getUpsellItems()}
        isVisible={showUpsell}
        onSelectItem={handleSelectUpsellItem}
        onSkip={handleSkipUpsell}
      />

      {/* Animations */}
      <style>{`
        @keyframes scale-in {
          from { 
            opacity: 0;
            transform: scale(0.5);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes check {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        @keyframes shrink-bar {
          from { width: 100%; }
          to { width: 0%; }
        }
        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }
        .animate-check {
          animation: check 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}
